import { useContext, useState, useCallback } from 'react';
import Assessments from '@/utils/api/odas/assessments';
import { languageContext } from '@/context/language.context';

/**
 * Hook to manage a cache of assessment details, useful for checking required_for_intake
 * and other assessment properties without repeatedly loading them.
 */
export const useAssessmentCache = () => {
  const [assessmentCache, setAssessmentCache] = useState({});
  const [loadingAssessments, setLoadingAssessments] = useState({});
  const { currentLanguage } = useContext(languageContext);

  /**
   * Load an assessment by ID and cache it
   * Returns the cached assessment if already loaded
   */
  const loadAssessment = useCallback(
    async (assessmentId, agencyClientId = null) => {
      if (!assessmentId) {
        return null;
      }

      // Return cached assessment if available
      if (assessmentCache[assessmentId]) {
        return assessmentCache[assessmentId];
      }

      // Return loading promise if already loading
      if (loadingAssessments[assessmentId]) {
        return loadingAssessments[assessmentId];
      }

      // Load the assessment
      const loadPromise = Assessments.getAssessmentBySlug(assessmentId, agencyClientId, { locale: currentLanguage })
        .then((response) => {
          const assessment = response?.data?.assessment;
          setAssessmentCache((prev) => ({
            ...prev,
            [assessmentId]: assessment
          }));
          setLoadingAssessments((prev) => {
            const newState = { ...prev };
            delete newState[assessmentId];
            return newState;
          });
          return assessment;
        })
        .catch(() => {
          // Cache null to avoid repeated failed requests
          setAssessmentCache((prev) => ({
            ...prev,
            [assessmentId]: null
          }));
          setLoadingAssessments((prev) => {
            const newState = { ...prev };
            delete newState[assessmentId];
            return newState;
          });
          return null;
        });

      setLoadingAssessments((prev) => ({
        ...prev,
        [assessmentId]: loadPromise
      }));

      return loadPromise;
    },
    [assessmentCache, loadingAssessments]
  );

  /**
   * Load multiple assessments in parallel
   */
  const loadAssessments = useCallback(
    async (assessmentIds, agencyClientId = null) => {
      const uniqueIds = [...new Set(assessmentIds)].filter(Boolean);
      const promises = uniqueIds.map((id) => loadAssessment(id, agencyClientId));
      return Promise.all(promises);
    },
    [loadAssessment]
  );

  /**
   * Check if an assessment is required for intake
   * Returns false if assessment is not loaded or doesn't exist
   */
  const isRequiredForIntake = useCallback(
    (assessmentId) => {
      if (!assessmentId || !assessmentCache[assessmentId]) {
        return false;
      }
      return assessmentCache[assessmentId]?.required_for_intake === true;
    },
    [assessmentCache]
  );

  /**
   * Check if an assessment is public for intake -- i.e. anyone can use it as an intake
   * assessment, rather than only the author org's referral partners.
   * Returns false if assessment is not loaded or doesn't exist, so callers fail closed.
   */
  const isPublicForIntake = useCallback(
    (assessmentId) => {
      if (!assessmentId || !assessmentCache[assessmentId]) {
        return false;
      }
      return assessmentCache[assessmentId]?.public_for_intake === true;
    },
    [assessmentCache]
  );

  /**
   * Get a cached assessment by ID
   */
  const getAssessment = useCallback(
    (assessmentId) => {
      return assessmentCache[assessmentId] || null;
    },
    [assessmentCache]
  );

  /**
   * Check if any assessments are currently loading
   */
  const isLoading = useCallback(() => {
    return Object.keys(loadingAssessments).length > 0;
  }, [loadingAssessments]);

  return {
    loadAssessment,
    loadAssessments,
    isRequiredForIntake,
    isPublicForIntake,
    getAssessment,
    assessmentCache,
    isLoading
  };
};

export default useAssessmentCache;
